// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@adl/App/Components/Common/XngZone";
import { color } from "@adl/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { css } from "styled-components";

const enum fields {
    fullwidth = "fullwidth",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.fullwidth]: boolean;
    };
}

export interface SolutionPageLayoutStyles {
    mainWrapper?: InjectableCss;
    contentContainer?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
}

export const solutionPageLayoutStyles: SolutionPageLayoutStyles = {
    contentContainer: {
        gap: 22,
    },
    leftColumnGridItem: {
        width: [12, 12, 12, 9, 9],
        css: css`
            flex-direction: column;
            div[class^="ContentWrapper-"] {
                & > h2 {
                    font-size: 28px;
                    line-height: 36px;
                    padding-top: 16px;
                    border-top: 1px solid ${color("common.border")};
                    margin-bottom: 24px;
                }
                & > h3 {
                    font-size: 24px;
                    line-height: 32px;
                    padding-top: 16px;
                    border-top: 1px solid ${color("common.border")};
                    margin-bottom: 24px;
                    margin-top: 36px;
                }
                & > .solutionDescription {
                    margin-bottom: 32px;
                }
            }
        `,
    },
    rightColumnGridItem: {
        width: [12, 12, 12, 3, 3],
        css: css`
            .solutionsWrapper > div {
                flex-direction: column;
                width: 100%;
                margin: 0;
                > div {
                    max-width: 100%;
                }
            }
            div[data-test-selector^="SolutionCard-"] {
                width: 100%;
                border: 1px solid ${color("common.ui6")};
                padding-bottom: 16px;
                & > *:not(:first-child) {
                    padding: 0 14px 0 16px;
                }
                h3 {
                    font-size: 20px;
                    line-height: 28px;
                }
                & > a > div {
                    max-height: 180px;
                }
                .link-arrow-right {
                    height: 16px;
                    width: 16px;
                }
            }
        `,
    },
};

const styles = solutionPageLayoutStyles;

type Props = OwnProps;

const SolutionPageLayout = ({ id, fields: { fullwidth } }: Props) => {
    return (
        <StyledWrapper {...styles.mainWrapper}>
            <GridContainer {...styles.contentContainer}>
                <GridItem {...styles.leftColumnGridItem}>
                    <XngZone contentId={id} zoneName="Content01" hint="Zone For Solution Content" />
                </GridItem>
                {!fullwidth && (
                    <GridItem {...styles.rightColumnGridItem}>
                        <XngZone contentId={id} zoneName="Content02" hint="Zone For Other Solutions Listing" />
                    </GridItem>
                )}
            </GridContainer>
            <XngZone contentId={id} zoneName="Content03" hint="Zone For Product Carousel" />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: SolutionPageLayout,
    definition: {
        group: "Xngage Custom" as any,
        displayName: "Solution Page Layout",
        fieldDefinitions: [
            {
                name: fields.fullwidth,
                displayName: "Full width",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
